import { useEffect, useState } from 'react';
import { AlertTriangle, Wallet } from 'lucide-react';
import { Field, Select, Input } from './ui/Field';
import { BudgetBar } from './BudgetBar';
import { budgetService, type PersonUsage } from '../services/budgetService';
import { usageService } from '../services/usageService';
import { formatBRL } from '../lib/utils';
import type { Area, Person, CostControl } from '../types';

// Editor de controle de custo reutilizado em Agente e Fluxo.
// Vincula a entidade a uma área/responsável e valida o teto contra o budget da pessoa.
export function CostControlEditor({
  value,
  onChange,
  entityId,
}: {
  value: CostControl;
  onChange: (c: CostControl) => void;
  entityId: string;
}) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [usage, setUsage] = useState<PersonUsage | null>(null);
  // Gasto real do mês desta entidade (ledger), por agente ou fluxo.
  const [spentReal, setSpentReal] = useState(0);

  useEffect(() => {
    Promise.all([budgetService.listAreas(), budgetService.listPeople()]).then(([a, p]) => {
      setAreas(a);
      setPeople(p);
    });
    usageService
      .summary()
      .then((s) => setSpentReal((s.byAgent[entityId]?.costUsd ?? 0) + (s.byFlow[entityId]?.costUsd ?? 0)))
      .catch(() => setSpentReal(0));
  }, [entityId]);

  const peopleInArea = people.filter((p) => p.areaId === value.areaId);
  const person = people.find((p) => p.id === value.personId);

  useEffect(() => {
    if (person) budgetService.personUsage(person, entityId).then(setUsage);
    else setUsage(null);
  }, [person?.id, value.monthlyCapBRL, entityId, person]);

  // remaining exclui a alocação desta entidade → o teto desta entidade deve caber aqui.
  const remaining = usage?.remaining ?? 0;
  const over = !!person && value.monthlyCapBRL > remaining;

  function setArea(areaId: string) {
    // troca de área limpa a pessoa se ela não pertencer à nova área
    const keepPerson = people.find((p) => p.id === value.personId && p.areaId === areaId);
    onChange({ ...value, areaId: areaId || undefined, personId: keepPerson?.id });
  }

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Área" className="mb-0">
          <Select value={value.areaId ?? ''} onChange={(e) => setArea(e.target.value)}>
            <option value="">Nenhuma</option>
            {areas.map((a) => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </Select>
        </Field>
        <Field label="Responsável (custo)" className="mb-0">
          <Select
            value={value.personId ?? ''}
            disabled={!value.areaId}
            onChange={(e) => onChange({ ...value, personId: e.target.value || undefined })}
          >
            <option value="">{value.areaId ? 'Selecione…' : 'Escolha a área'}</option>
            {peopleInArea.map((p) => (
              <option key={p.id} value={p.id}>{p.name}</option>
            ))}
          </Select>
        </Field>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Teto mensal (US$)" hint="alocado ao responsável" className="mb-0">
          <Input
            type="number"
            min={0}
            step="50"
            value={value.monthlyCapBRL}
            onChange={(e) => onChange({ ...value, monthlyCapBRL: Math.max(0, Number(e.target.value)) })}
          />
        </Field>
        <Field label="Teto por execução (US$)" hint="0 = sem limite · estima e bloqueia antes de chamar" className="mb-0">
          <Input
            type="number"
            min={0}
            step="1"
            value={value.perRunCapBRL}
            onChange={(e) => onChange({ ...value, perRunCapBRL: Math.max(0, Number(e.target.value)) })}
          />
        </Field>
      </div>

      <Field label="Ao estourar o teto" className="mb-0">
        <Select value={value.onExceed} onChange={(e) => onChange({ ...value, onExceed: e.target.value as CostControl['onExceed'] })}>
          <option value="warn">Avisar e continuar</option>
          <option value="require_approval">Exigir aprovação para continuar</option>
          <option value="block">Bloquear a execução</option>
        </Select>
      </Field>

      {/* Indicador do budget do responsável */}
      {person && usage && (
        <div className="rounded-xl border border-line bg-cloud/60 p-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-medium text-ink">
            <Wallet className="h-3.5 w-3.5 text-slate" />
            Budget de {person.name}
          </div>
          <BudgetBar used={usage.allocated + value.monthlyCapBRL} total={usage.budget} />
          <div className="mt-2 text-xs text-slate">
            Já alocado a outros itens: <strong className="text-ink">{formatBRL(usage.allocated)}</strong> · Livre para este:{' '}
            <strong className={over ? 'text-danger' : 'text-ink'}>{formatBRL(remaining)}</strong>
          </div>
          {over && (
            <div className="mt-2 flex items-start gap-2 rounded-lg border border-danger/30 bg-danger/10 p-2 text-xs text-danger">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              Teto excede o budget livre do responsável. Reduza o teto ou ajuste o budget da pessoa.
            </div>
          )}
        </div>
      )}

      {/* Gasto real do mês vs teto */}
      {value.monthlyCapBRL > 0 && (
        <div className="rounded-xl border border-line bg-white p-3">
          <BudgetBar used={spentReal} total={value.monthlyCapBRL} label="Gasto no mês (real)" />
        </div>
      )}
    </div>
  );
}
